<script setup>
import { ref, toRefs, computed, watch } from 'vue';
import { formatBytes } from '@/services/utils';
import { useUpload } from '@/composables/useUpload';

const props = defineProps({
  accept: {
    type: String,
    default: '*/*',
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  roundPreview: {
    type: Boolean,
    default: false,
  },
  uploading: {
    type: Boolean,
    default: false,
  },
  progress: {
    type: Number,
    default: 0,
  },
});
const {
  accept: acceptProp,
  required,
  disabled,
  uploading,
  progress,
} = toRefs(props);

const accept = computed(() => {
  const acceptLowercase = acceptProp.value.toLowerCase();
  const accepts = acceptLowercase
    .split(',')
    .map((type) => type.trim())
    .join(',');
  return accepts;
});

const {
  upload: uploadFile,
  uploading: uploadingFile,
  onProgress: onUploadProgress,
  onDone: onUploadDone,
  onError: onUploadError,
} = useUpload();

const useUploadProgress = ref(0);
onUploadProgress((progress) => {
  useUploadProgress.value = progress;
});

const isUploading = computed(() => uploadingFile.value || uploading.value);
const uploadProgress = computed(
  () => progress.value || useUploadProgress.value
);

function upload(signedUrl) {
  return uploadFile(signedUrl, selectedFile.value);
}

const isPreviewOpen = ref(false);

defineExpose({
  upload,
  progress: useUploadProgress,
  onDone: onUploadDone,
  onError: onUploadError,
  isPreviewOpen,
});

const selectedFile = defineModel({
  type: [Object, String],
  default: null,
});

const previewImage = ref(null);
const error = ref(null);
const dirtyInput = ref(false);

const fileIsURL = computed(() => {
  if (selectedFile.value && typeof selectedFile.value === 'string') return true;
  return false;
});

const fileName = computed(() => {
  if (selectedFile.value && typeof selectedFile.value === 'string')
    return selectedFile.value;
  else if (selectedFile.value && selectedFile.value.name)
    return selectedFile.value.name;
  return '';
});

const fileType = computed(() => {
  if (!selectedFile.value) return null;
  return selectedFile.value.type;
});

const isImage = computed(() => fileType.value?.startsWith('image/'));
const isVideo = computed(() => fileType.value?.startsWith('video/'));
const isAudio = computed(() => fileType.value?.startsWith('audio/'));
const isOther = computed(
  () => !isImage.value && !isVideo.value && !isAudio.value
);

// Accept other if no type is specified or multiple types are specified
const acceptImage = computed(() => accept.value.includes('image/'));
const acceptVideo = computed(() => accept.value.includes('video/'));
const acceptAudio = computed(() => accept.value.includes('audio/'));
const acceptOther = computed(() => {
  const acceptedTypes = [
    acceptImage.value,
    acceptVideo.value,
    acceptAudio.value,
  ].filter((val) => !!val);
  return acceptedTypes.length === 0 || acceptedTypes.length > 1;
});

const previewIcon = computed(() => {
  if (isImage.value || (isOther.value && acceptImage.value))
    return 'icon-[material-symbols--image-outline]';
  if (isVideo.value || (isOther.value && acceptVideo.value))
    return 'icon-[material-symbols--video-file-outline]';
  if (isAudio.value || (isOther.value && acceptAudio.value))
    return 'icon-[material-symbols--audio-file-outline]';
  return 'icon-[mdi--file-outline]';
});

watch(
  selectedFile,
  (newFile) => {
    if (newFile && typeof newFile === 'string' && newFile.length > 0) {
      if (acceptImage.value) {
        previewImage.value = newFile;
      }
      dirtyInput.value = true;
    } else if (newFile && newFile instanceof File) {
      onFileSelect(newFile);
    } else {
      selectedFile.value = null;
      previewImage.value = null;
    }
  },
  { immediate: true }
);

function onFileChange(e) {
  onFileSelect(e.target.files[0]);
}

function onFileSelect(file) {
  if (!file) {
    return;
  }
  error.value = null;
  dirtyInput.value = true;
  selectedFile.value = file;
  previewImage.value = null;
  useUploadProgress.value = 0;

  // if Image: show preview
  const isImage = file.type.startsWith('image/');
  if (!isImage) return;
  const reader = new FileReader();
  reader.onload = function () {
    previewImage.value = reader.result;
  };
  reader.readAsDataURL(file);
}

const fileInput = ref();
function selectFile(e) {
  fileInput.value.click();
}

function removeFile() {
  selectedFile.value = null;
  previewImage.value = null;
  if (dirtyInput.value && required.value) {
    error.value = { message: 'File is required' };
  }
}

// DRAG AND DROP FUNCTIONALITY
const isOverDropZone = ref(false);
const draggedInvalid = ref(null);

// Handle the drop behavior
// Check the file type is valid for the accept
function onDragDrop(e) {
  e.preventDefault();
  e.stopPropagation();
  isOverDropZone.value = false;
  const files = getDragAndDropEventFiles(e);
  const file = files[0];
  if (!isTypeValid(file.type)) {
    error.value = { message: 'Invalid file type' };
    draggedInvalid.value = false;
    return;
  }
  onFileSelect(file);
}

// Highlight the zone and check the file type is valid
function onDragEnter(e) {
  e.preventDefault();
  e.stopPropagation();
  isOverDropZone.value = true;

  const types = getDragAndDropEventTypes(e);
  const type = types[0];
  if (!isTypeValid(type)) {
    draggedInvalid.value = true;
    return;
  }
  draggedInvalid.value = false;
}
function onDragLeave(e) {
  e.preventDefault();
  e.stopPropagation();
  isOverDropZone.value = false;
  draggedInvalid.value = false;
}
// Required to prevent default browser behavior
function onDragOver(e) {
  e.preventDefault();
  e.stopPropagation();
}

const dragAndDropDataTypes = computed(() => accept.value.split(','));

// AI function to validate file type when drag and drop hover
// A function named isTypeValid that accepts a file type, compares it to the accept prop, and returns a boolean
// This function should also account for wildcard types (e.g. image/*)
// This function should also account for multiple types (e.g. image/jpeg,image/png)
// This function should also account for multiple types with wildcards (e.g. image/*,video/*)
function isTypeValid(fileType) {
  const validTypes = dragAndDropDataTypes.value;
  if (validTypes.includes(fileType)) return true;
  if (validTypes.includes('*/*')) return true;
  if (validTypes.includes(`${fileType.split('/')[0]}/*`)) return true;
  return false;
}

// Return a list of the file types being dragged
// Cannot use getDragAndDropEventFiles because there are no files only file types for security reasons
function getDragAndDropEventTypes(e) {
  if (e.dataTransfer.items) {
    const types = [...e.dataTransfer.items]
      .filter((item) => item.kind === 'file')
      .map((item) => item.type);
    return types;
  } else {
    const types = [...e.dataTransfer.files].map((file) => file.type);
    return types;
  }
}
// Return a list of the files dropped
function getDragAndDropEventFiles(e) {
  if (e.dataTransfer.items) {
    // Use DataTransferItemList interface to access the file(s)
    const files = [...e.dataTransfer.items]
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile());
    return files;
  } else {
    // Use DataTransfer interface to access the file(s)
    const files = [...e.dataTransfer.files];
    return files;
  }
}

// Need to handle previewing state so modal can disable escape key close
function hidePreview() {
  setTimeout(() => (isPreviewOpen.value = false), 50);
}
</script>
<template>
  <div>
    <Message v-if="error" severity="error" @close="error = null" closable>
      {{ error.message }}
    </Message>
    <div>
      <div
        @drop="onDragDrop"
        @dragover="onDragOver"
        @dragenter="onDragEnter"
        class="relative"
        :class="{
          'pointer-events-none cursor-not-allowed opacity-50': disabled,
        }"
      >
        <!-- File preview section and uploading -->
        <div
          v-if="selectedFile"
          class="flex flex-row items-center gap-4 rounded-md border border-surface-300 p-4 dark:border-surface-600"
        >
          <!-- Image or Icon -->
          <Image
            v-if="previewImage"
            :src="previewImage"
            :image-class="
              roundPreview ? 'w-14 rounded-full object-cover h-14' : 'h-14'
            "
            :class="
              roundPreview ? 'h-14 w-14 overflow-hidden rounded-full' : 'h-14'
            "
            preview
            alt="Image preview"
            @show="isPreviewOpen = true"
            @hide="hidePreview"
          />
          <i v-else class="text-6xl" :class="previewIcon" />

          <!-- File name or URL (first line)-->
          <div class="flex flex-1 flex-col gap-3">
            <div class="flex flex-row justify-between">
              <div class="mr-2 w-0 flex-1">
                <!-- <a
                v-if="fileIsURL"
                :href="selectedFile"
                target="_blank"
                class="detail-link flex items-center"
              >
                <div
                  class="overflow-hidden overflow-ellipsis whitespace-nowrap text-sm text-surface-950 dark:text-surface-50"
                >
                  {{ fileName }}
                </div>
                <i class="icon-[prime--external-link]" />
              </a> -->
                <div
                  class="overflow-hidden overflow-ellipsis whitespace-nowrap text-sm text-surface-950 dark:text-surface-50"
                >
                  {{ fileName }}
                </div>
              </div>
              <Button
                :disabled="isUploading"
                @click="removeFile"
                aria-label="Remove file"
                size="small"
                text
                severity="secondary"
                rounded
                class="-m-1 !p-1"
              >
                <i class="icon-[prime--times] text-2xl" />
              </Button>
            </div>

            <!-- File size and Uploading progress (2nd line) -->
            <div
              class="flex flex-row items-center gap-2 text-sm text-surface-600 dark:text-surface-300"
            >
              <template v-if="isUploading || uploadProgress === 100">
                <div v-if="selectedFile.size">
                  {{ formatBytes((selectedFile.size * uploadProgress) / 100) }}
                  of
                  {{ formatBytes(selectedFile.size) }}
                </div>
                <span v-if="selectedFile.size && uploadProgress !== 100">
                  &middot;
                </span>
                <template v-if="uploadProgress !== 100">
                  <ProgressBar
                    :value="uploadProgress"
                    class="h-[6px] flex-1"
                    :mode="
                      uploadProgress === 0 && isUploading
                        ? 'indeterminate'
                        : 'determinate'
                    "
                    >&nbsp;</ProgressBar
                  >
                  <span>Uploading</span>
                </template>
              </template>
              <template v-else-if="!isUploading && !fileIsURL">
                {{ formatBytes(selectedFile.size) }}
              </template>
              <div
                v-if="fileIsURL || uploadProgress === 100"
                class="flex gap-1 text-green-500"
              >
                <i class="icon-[prime--check-circle] text-xl" />
                <span>Uploaded</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Select file section.  Supports click and drag & drop -->
        <div
          v-else
          @click="selectFile"
          class="group relative flex cursor-pointer flex-col items-center justify-center gap-2.5 rounded-md border border-dashed border-surface-400 p-4 hover:border-primary-700/50 hover:bg-primary-100/10 dark:border-surface-600 dark:bg-surface-900 dark:hover:border-primary-400/50 dark:hover:bg-surface-900/10"
          :class="{
            'border-primary-700/50 bg-primary-100/10 dark:border-primary-400/50 dark:bg-surface-900/10':
              isOverDropZone,
          }"
        >
          <i class="icon-[streamline--upload-file-solid] text-4xl" />
          <div class="text-sm font-normal">
            <span class="underline group-hover:font-semibold"
              >Click to select</span
            >
            <span> or drag and drop</span>
          </div>
        </div>

        <!-- Overlay when dragging a file over the drop zone -->
        <!-- after: Tailwind/CSS is required to overlay the children of this section -->
        <!-- Otherwise, the dragleave will trigger when you mouse over a child even though you are still in the parent -->
        <div
          v-show="isOverDropZone"
          @dragleave="onDragLeave"
          @dragend="onDragLeave"
          class="absolute inset-0 flex flex-col items-center justify-center gap-2.5 rounded-md border border-primary-300 bg-primary-100/50 backdrop-blur-md after:absolute after:inset-0 after:rounded-md after:opacity-0 after:content-[''] dark:border-surface-600"
          :class="{
            '!border-red-500 !bg-red-200/50': draggedInvalid,
          }"
        >
          <template v-if="draggedInvalid || isUploading">
            <i class="icon-[mdi--file-cancel-outline] text-4xl text-red-700" />
            <div class="font-medium text-red-700">
              <template v-if="draggedInvalid">Invalid file type</template>
              <template v-if="isUploading">Uploading</template>
            </div>
          </template>
          <template v-else>
            <i class="icon-[material-symbols--note-add-outline] text-4xl" />

            <div>Drop to select</div>
          </template>
        </div>
        <input
          type="file"
          class="hidden"
          ref="fileInput"
          @change="onFileChange"
          :accept="accept"
        />
      </div>
      <small
        v-if="accept"
        class="mt-1 flex items-center gap-1 text-surface-500 dark:text-surface-400"
        :class="{
          'text-red-500': draggedInvalid,
        }"
      >
        <i class="icon-[mdi--info-outline]"></i
        ><span class="uppercase">{{
          accept
            .split(',')
            .map((type) => {
              if (!type || type === '*/*') return 'Any file';
              if (type.endsWith('/*')) return `Any ${type.split('/')[0]}`;
              return type.split('/')[1];
            })
            .join(', ')
        }}</span>
      </small>
    </div>
  </div>
</template>
